import type { ReactNode } from 'react'
import type { HourForecast } from '@/lib/server/forecast'
import type { NamedStar, NightEvents, PlanetKey, PlanetNight, ViewingWindow } from '@/lib/astro/ephemeris'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Body, Equator, Illumination, MoonPhase, SearchMoonQuarter, NextMoonQuarter } from 'astronomy-engine'
import { ArrowUpRight, ChevronLeft, ChevronRight, Pause, Play, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getMoonState } from '@/lib/astro/moon'
import { loadStars, starName } from '@/lib/astro/catalogue'
import { COMPASS_WORDS, compassPoint, observer } from '@/lib/astro/sky'
import { prefersReducedMotion } from '@/lib/preferences'
import { Sheet } from '@/components/generals/sheet'
import { Reveal, RevealLines } from '@/components/generals/reveal'
import { FadeLink } from '@/components/generals/fade-link'
import { SkyChart } from '@/components/sky/sky-chart'
import { MoonSphere } from '@/components/sky/moon-sphere'
import { ViewingGauge, verdictFor } from '@/components/sky/viewing-gauge'

import {
    AU_KM,
    bestViewingWindow,
    nightEvents,
    planetsForNight,
    skySnapshot,
    tonightLine
} from '@/lib/astro/ephemeris'

import {
    addDays,
    dateKey,
    formatDuration,
    formatKey,
    formatTime,
    isDateKey,
    nightKeyFor
} from '@/lib/time'

type Outlook = {
    available: boolean
    reason?: string
    hours: HourForecast[]
    cloud: number | null
    seeing: number | null
    transparency: number | null
    summary: string
    updatedAt?: string | null
}

type Detail =
    | { kind: 'planet', key: PlanetKey }
    | { kind: 'moon' }
    | { kind: 'sun' }
    | { kind: 'window' }
    | { kind: 'conditions' }

const MINUTE = 60 * 1000

interface SkyTonightProps {
    variant?: 'home' | 'page'
}

const directionWords = (azimuth: number) => COMPASS_WORDS[compassPoint(azimuth)]

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

/** The night the sky belongs to right now: until sunrise it is still the previous evening. */
const nightKeyNow = (now: Date): string => {
    const today = dateKey(now)
    const previous = addDays(today, -1)
    const sunrise = nightEvents(previous).sunrise

    if (!sunrise) return nightKeyFor(now)

    return now.getTime() < sunrise.getTime() ? previous : today
}

/** Scores the night from 0 to 10 from cloud, seeing, transparency and moonlight. */
const qualityScore = (outlook: Outlook | null, moonFraction: number, moonUpInWindow: boolean): number | null => {
    if (!outlook?.available || outlook.cloud === null) return null

    const clear = Math.pow(1 - outlook.cloud / 100, 1.35)
    const steadiness = ((outlook.seeing ?? 3) + (outlook.transparency ?? 3)) / 10
    const moonlight = moonUpInWindow ? moonFraction * 0.28 : 0

    return clamp(10 * clear * (0.55 + 0.45 * steadiness) * (1 - moonlight), 0, 10)
}

const Row = ({
    label,
    onClick,
    children,
    className
}: {
    label: string
    onClick: () => void
    children: ReactNode
    className?: string
}): ReactNode => (
    <button
        type='button'
        onClick={onClick}
        className={cn('group relative block w-full border-t border-hair py-6 text-left transition-colors duration-500 hover:bg-ink-2/60 md:px-2', className)}
    >
        <span className='label-mono flex items-center justify-between text-star-3'>
            {label}
            <ArrowUpRight aria-hidden className='size-3.5 text-brass opacity-0 transition-all duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:opacity-100' strokeWidth={1.25} />
        </span>
        <span className='mt-3 block'>
            {children}
        </span>
    </button>
)

const DataPair = ({ term, value }: { term: string, value: ReactNode }): ReactNode => (
    <div className='flex items-baseline justify-between gap-6 border-b border-hair py-3'>
        <dt className='label-mono text-star-3'>
            {term}
        </dt>
        <dd className='data-mono text-right text-star'>
            {value}
        </dd>
    </div>
)

export const SkyTonight = ({ variant = 'home' }: SkyTonightProps): ReactNode => {
    const [nightKey, setNightKey] = useState<string | null>(null)
    const [minute, setMinute] = useState(0)
    const [detail, setDetail] = useState<Detail | null>(null)
    const [outlook, setOutlook] = useState<Outlook | null>(null)
    const [outlookState, setOutlookState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
    const [namedStars, setNamedStars] = useState<NamedStar[]>([])
    const [playing, setPlaying] = useState(false)
    const [todayKey, setTodayKey] = useState<string | null>(null)

    useEffect(() => {
        const now = new Date()
        const key = nightKeyNow(now)
        const events = nightEvents(key)
        const start = events.sunset ?? events.darkStart
        const end = events.sunrise ?? events.darkEnd
        const span = Math.max(60, (end.getTime() - start.getTime()) / MINUTE)
        const nowMinute = (now.getTime() - start.getTime()) / MINUTE
        const tenPm = (events.darkStart.getTime() - start.getTime()) / MINUTE + 30

        setTodayKey(key)
        setNightKey(key)
        setMinute(Math.round(clamp(nowMinute >= 0 && nowMinute <= span ? nowMinute : Math.max(tenPm, 90), 0, span)))

        loadStars().then(catalogue => {
            const stars: NamedStar[] = []

            for (let i = 0; i < catalogue.count && catalogue.mag[i] < 2.3; i++) {
                const name = starName(catalogue.hip[i])

                if (name) stars.push({ name, vector: [catalogue.xyz[i * 3], catalogue.xyz[i * 3 + 1], catalogue.xyz[i * 3 + 2]] })
            }

            setNamedStars(stars)
        }).catch(() => undefined)
    }, [])

    const night = useMemo(() => {
        if (!nightKey) return null

        const events = nightEvents(nightKey)
        const planets = planetsForNight(events)
        const start = events.sunset ?? events.darkStart
        const end = events.sunrise ?? events.darkEnd

        return { events, planets, start, end, span: Math.max(60, Math.round((end.getTime() - start.getTime()) / MINUTE)) }
    }, [nightKey])

    useEffect(() => {
        if (!night) return

        const controller = new AbortController()

        setOutlookState('loading')
        setOutlook(null)

        fetch(`/api/sky/forecast?start=${encodeURIComponent(night.start.toISOString())}&end=${encodeURIComponent(night.end.toISOString())}`, { signal: controller.signal })
            .then(async response => {
                const data = await response.json() as Outlook

                setOutlook(data)
                setOutlookState(response.ok ? 'ready' : 'error')
            })
            .catch(error => {
                if ((error as Error).name !== 'AbortError') setOutlookState('error')
            })

        return () => controller.abort()
    }, [night])

    useEffect(() => {
        if (!playing || !night) return

        const timer = window.setInterval(() => {
            setMinute(value => {
                if (value >= night.span) {
                    setPlaying(false)
                    return night.span
                }

                return Math.min(night.span, value + 4)
            })
        }, prefersReducedMotion() ? 400 : 90)

        return () => window.clearInterval(timer)
    }, [playing, night])

    const time = useMemo(() => (night ? new Date(night.start.getTime() + minute * MINUTE) : null), [night, minute])
    const bodies = useMemo(() => (time ? skySnapshot(time) : []), [time])

    const cloudAt = useCallback((at: Date): number | null => {
        if (!outlook?.available || !outlook.hours.length) return null

        let best: HourForecast | null = null
        let bestGap = Infinity

        for (const hour of outlook.hours) {
            const gap = Math.abs(new Date(hour.time).getTime() - at.getTime())

            if (gap < bestGap) {
                bestGap = gap
                best = hour
            }
        }

        return best && bestGap < 4 * 3600 * 1000 ? best.cloud : null
    }, [outlook])

    const viewingWindow: ViewingWindow | null = useMemo(() => (night ? bestViewingWindow(night.events, cloudAt) : null), [night, cloudAt])

    const moon = useMemo(() => {
        if (!night || !time) return null

        const state = getMoonState(time)
        const illumination = Illumination(Body.Moon, time)
        const equator = Equator(Body.Moon, time, observer, true, true)
        const age = (MoonPhase(time) / 360) * 29.530588

        let quarter = SearchMoonQuarter(time)
        let nextNew: Date | null = null
        let nextFull: Date | null = null

        for (let i = 0; i < 6 && (!nextNew || !nextFull); i++) {
            if (quarter.quarter === 0 && !nextNew) nextNew = quarter.time.date
            if (quarter.quarter === 2 && !nextFull) nextFull = quarter.time.date
            quarter = NextMoonQuarter(quarter)
        }

        return {
            ...state,
            phaseAngle: illumination.phase_angle,
            distanceKm: equator.dist * AU_KM,
            age,
            nextNew,
            nextFull
        }
    }, [night, time])

    const moonUpInWindow = useMemo(() => {
        if (!night || !viewingWindow) return false

        const middle = new Date((viewingWindow.start.getTime() + viewingWindow.end.getTime()) / 2)
        const snapshot = skySnapshot(middle).find(body => body.key === 'moon')

        return Boolean(snapshot && snapshot.alt > 0)
    }, [night, viewingWindow])

    const score = useMemo(() => qualityScore(outlook, moon?.fraction ?? 0, moonUpInWindow), [outlook, moon, moonUpInWindow])
    const headline = useMemo(() => (night ? tonightLine(night.events, night.planets, namedStars) : ''), [night, namedStars])

    const visiblePlanets = night?.planets.filter(planet => planet.visibleFrom) ?? []
    const hiddenPlanets = night?.planets.filter(planet => !planet.visibleFrom && planet.nakedEye) ?? []

    const changeNight = (key: string) => {
        if (!isDateKey(key)) return

        const events = nightEvents(key)
        const start = events.sunset ?? events.darkStart

        setPlaying(false)
        setNightKey(key)
        setMinute(Math.max(60, Math.round((events.darkStart.getTime() - start.getTime()) / MINUTE) + 30))
    }

    const resetToNow = () => {
        const now = new Date()
        const key = nightKeyNow(now)
        const events = nightEvents(key)
        const start = events.sunset ?? events.darkStart
        const end = events.sunrise ?? events.darkEnd
        const span = Math.max(60, (end.getTime() - start.getTime()) / MINUTE)
        const nowMinute = (now.getTime() - start.getTime()) / MINUTE

        setPlaying(false)
        setTodayKey(key)
        setNightKey(key)
        setMinute(Math.round(clamp(nowMinute, 0, span)))
    }

    const markerPercent = (date: Date | null) => {
        if (!date || !night) return null

        const value = ((date.getTime() - night.start.getTime()) / MINUTE / night.span) * 100

        return value >= 0 && value <= 100 ? value : null
    }

    const isTonight = nightKey !== null && nightKey === todayKey
    const titleLines = variant === 'home' ? ['What is above', 'the dome tonight'] : ['The sky above', 'Hollowmoor Ridge']

    const selectedPlanet = detail?.kind === 'planet' ? night?.planets.find(planet => planet.key === detail.key) ?? null : null
    const selectedBody = detail?.kind === 'planet' ? bodies.find(body => body.key === detail.key) ?? null : null

    const sheetTitle = detail?.kind === 'planet'
        ? selectedPlanet?.name ?? ''
        : detail?.kind === 'moon'
            ? 'The Moon'
            : detail?.kind === 'sun'
                ? 'Sunset and darkness'
                : detail?.kind === 'window'
                    ? 'Best viewing window'
                    : 'Viewing conditions'

    return (
        <section id='sky-tonight' aria-labelledby='sky-tonight-title' className='relative scroll-mt-16 py-28 md:py-36'>
            <div className='container-page'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <p className='label-mono text-brass'>
                            {variant === 'home' ? 'Sky Tonight' : 'Live from the observatory'}
                        </p>
                        <RevealLines
                            as={variant === 'home' ? 'h2' : 'h1'}
                            id='sky-tonight-title'
                            lines={titleLines}
                            className='display-serif mt-5 text-5xl text-star md:text-7xl'
                        />
                    </div>
                    <Reveal variant='wipe' className='lg:col-span-5'>
                        <p className='text-star-2'>
                            Calculated for our roof deck at 51.15° north, from the real positions of the stars, Moon and planets. Drag the clock through the night, or choose another date.
                        </p>
                    </Reveal>
                </div>

                <div className='mt-16 grid gap-14 lg:grid-cols-12 lg:gap-16'>
                    <div className='lg:col-span-7'>
                        <div className='mx-auto max-w-[44rem]'>
                            {time ? (
                                <SkyChart
                                    time={time}
                                    bodies={bodies}
                                    selected={detail?.kind === 'planet' ? detail.key : detail?.kind === 'moon' ? 'moon' : null}
                                    onSelect={key => setDetail(key === 'moon' ? { kind: 'moon' } : { kind: 'planet', key: key as PlanetKey })}
                                    label={`Chart of the sky above the observatory at ${formatTime(time)}`}
                                />
                            ) : (
                                <div className='flex aspect-square w-full items-center justify-center rounded-full border border-hair'>
                                    <p className='label-mono slow-pulse text-star-3'>
                                        Calculating tonight&apos;s sky
                                    </p>
                                </div>
                            )}

                            <div className='mt-10'>
                                <div className='flex flex-wrap items-end justify-between gap-4'>
                                    <div>
                                        <p className='label-mono text-star-3'>
                                            {nightKey ? `${isTonight ? 'Tonight' : 'Night of'} · ${formatKey(nightKey, 'long')}` : 'Tonight'}
                                        </p>
                                        <p className='mt-1 font-heading text-5xl font-light tabular-nums text-star' aria-live='polite'>
                                            {time ? formatTime(time) : '--:--'}
                                        </p>
                                    </div>
                                    <div className='flex items-center gap-2'>
                                        <button
                                            type='button'
                                            onClick={() => setPlaying(value => !value)}
                                            disabled={!night}
                                            aria-label={playing ? 'Pause the night' : 'Play through the night'}
                                            className='inline-flex size-10 items-center justify-center rounded-full border border-hair-2 text-star transition-colors hover:border-brass'
                                        >
                                            {playing ? <Pause aria-hidden className='size-4' strokeWidth={1.25} /> : <Play aria-hidden className='size-4' strokeWidth={1.25} />}
                                        </button>
                                        <button
                                            type='button'
                                            onClick={resetToNow}
                                            className='label-mono inline-flex h-10 items-center gap-2 rounded-full border border-hair-2 px-4 text-star-2 transition-colors hover:border-brass hover:text-star'
                                        >
                                            <RotateCcw aria-hidden className='size-3.5' strokeWidth={1.25} />
                                            Now
                                        </button>
                                    </div>
                                </div>

                                <div className='relative mt-6'>
                                    {night && (
                                        <div aria-hidden className='pointer-events-none absolute inset-x-0 top-0 h-7'>
                                            {(() => {
                                                const darkStart = markerPercent(night.events.astroDusk ?? night.events.nauticalDusk)
                                                const darkEnd = markerPercent(night.events.astroDawn ?? night.events.nauticalDawn)
                                                const windowStart = markerPercent(viewingWindow?.start ?? null)
                                                const windowEnd = markerPercent(viewingWindow?.end ?? null)

                                                return (
                                                    <>
                                                        {darkStart !== null && darkEnd !== null && (
                                                            <span className='absolute top-1/2 h-[3px] -translate-y-1/2 bg-ink-4' style={{ left: `${darkStart}%`, width: `${darkEnd - darkStart}%` }} />
                                                        )}
                                                        {windowStart !== null && windowEnd !== null && (
                                                            <span className='absolute top-1/2 h-[3px] -translate-y-1/2 bg-brass/70' style={{ left: `${windowStart}%`, width: `${Math.max(0.5, windowEnd - windowStart)}%` }} />
                                                        )}
                                                    </>
                                                )
                                            })()}
                                        </div>
                                    )}
                                    <label htmlFor='sky-time' className='sr-only'>
                                        Time of night
                                    </label>
                                    <input
                                        id='sky-time'
                                        type='range'
                                        className='instrument-range relative'
                                        min={0}
                                        max={night?.span ?? 600}
                                        step={5}
                                        value={minute}
                                        disabled={!night}
                                        onChange={event => {
                                            setPlaying(false)
                                            setMinute(Number(event.target.value))
                                        }}
                                        aria-valuetext={time ? formatTime(time) : undefined}
                                    />
                                    <div className='label-mono mt-2 flex justify-between text-[0.62rem] text-star-3'>
                                        <span>
                                            {`Sunset ${formatTime(night?.events.sunset)}`}
                                        </span>
                                        <span className='hidden text-brass sm:inline'>
                                            {viewingWindow ? `Best ${formatTime(viewingWindow.start)} to ${formatTime(viewingWindow.end)}` : ''}
                                        </span>
                                        <span>
                                            {`Sunrise ${formatTime(night?.events.sunrise)}`}
                                        </span>
                                    </div>
                                </div>

                                <div className='mt-8 flex flex-wrap items-end gap-3'>
                                    <button
                                        type='button'
                                        onClick={() => nightKey && changeNight(addDays(nightKey, -1))}
                                        aria-label='Previous night'
                                        className='inline-flex size-10 items-center justify-center rounded-full border border-hair-2 text-star-2 transition-colors hover:border-brass hover:text-star'
                                    >
                                        <ChevronLeft aria-hidden className='size-4' strokeWidth={1.25} />
                                    </button>
                                    <div className='min-w-[11rem]'>
                                        <label htmlFor='sky-date' className='label-mono text-star-3'>
                                            Any date
                                        </label>
                                        <input
                                            id='sky-date'
                                            type='date'
                                            className='field py-1.5 font-mono text-sm'
                                            value={nightKey ?? ''}
                                            onChange={event => changeNight(event.target.value)}
                                        />
                                    </div>
                                    <button
                                        type='button'
                                        onClick={() => nightKey && changeNight(addDays(nightKey, 1))}
                                        aria-label='Next night'
                                        className='inline-flex size-10 items-center justify-center rounded-full border border-hair-2 text-star-2 transition-colors hover:border-brass hover:text-star'
                                    >
                                        <ChevronRight aria-hidden className='size-4' strokeWidth={1.25} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='lg:col-span-5'>
                        <Reveal>
                            <p className='label-mono text-star-3'>
                                {isTonight ? 'Tonight, in a sentence' : 'That night, in a sentence'}
                            </p>
                            <p className='mt-4 font-heading text-[1.85rem] font-light leading-snug text-star md:text-[2.1rem]'>
                                {headline || 'Working out what is worth looking at.'}
                            </p>
                        </Reveal>

                        <div className='mt-10'>
                            <Row label='Moon' onClick={() => setDetail({ kind: 'moon' })}>
                                <span className='flex items-center gap-6'>
                                    <span className='w-24 shrink-0 sm:w-28'>
                                        {moon && <MoonSphere phaseAngle={moon.phaseAngle} waxing={moon.waxing} label={`The Moon, ${moon.name.toLowerCase()}, ${Math.round(moon.fraction * 100)} percent lit`} />}
                                    </span>
                                    <span className='block'>
                                        <span className='block font-heading text-3xl font-light text-star'>
                                            {moon ? moon.name : '—'}
                                        </span>
                                        <span className='data-mono mt-1 block text-star-2'>
                                            {moon ? `${Math.round(moon.fraction * 100)}% illuminated` : ''}
                                        </span>
                                        <span className='data-mono mt-1 block text-star-3'>
                                            {night ? `Rises ${formatTime(night.events.moonrise)} · Sets ${formatTime(night.events.moonset)}` : ''}
                                        </span>
                                    </span>
                                </span>
                            </Row>

                            <Row label='Sun and darkness' onClick={() => setDetail({ kind: 'sun' })}>
                                <span className='grid grid-cols-2 gap-x-6 gap-y-2'>
                                    <span className='data-mono text-star-2'>
                                        {`Sunset ${formatTime(night?.events.sunset)}`}
                                    </span>
                                    <span className='data-mono text-star-2'>
                                        {`Dark from ${formatTime(night?.events.darkStart)}`}
                                    </span>
                                    <span className='data-mono text-star-3'>
                                        {`Dark until ${formatTime(night?.events.darkEnd)}`}
                                    </span>
                                    <span className='data-mono text-star-3'>
                                        {`Sunrise ${formatTime(night?.events.sunrise)}`}
                                    </span>
                                </span>
                                {night?.events.darkness === 'nautical' && (
                                    <span className='mt-2 block text-sm text-star-3'>
                                        No full darkness tonight: the Sun stays too close below the horizon.
                                    </span>
                                )}
                            </Row>

                            <div className='border-t border-hair py-6 md:px-2'>
                                <p className='label-mono text-star-3'>
                                    Planets you can see
                                </p>
                                <ul className='mt-3'>
                                    {visiblePlanets.length === 0 && night && (
                                        <li className='py-2 text-star-2'>
                                            None of the planets clear the horizon in darkness tonight.
                                        </li>
                                    )}
                                    {visiblePlanets.map(planet => (
                                        <li key={planet.key}>
                                            <button
                                                type='button'
                                                onClick={() => setDetail({ kind: 'planet', key: planet.key })}
                                                className='group flex w-full items-baseline justify-between gap-4 border-b border-hair py-3 text-left transition-colors hover:border-brass/50'
                                            >
                                                <span className='flex items-baseline gap-3'>
                                                    <span className='font-heading text-2xl text-star'>
                                                        {planet.name}
                                                    </span>
                                                    {!planet.nakedEye && (
                                                        <span className='label-mono text-[0.6rem] text-star-3'>
                                                            Telescope
                                                        </span>
                                                    )}
                                                </span>
                                                <span className='data-mono text-right text-star-2'>
                                                    {`${formatTime(planet.visibleFrom)} to ${formatTime(planet.visibleTo)}`}
                                                    <span className='block text-[0.7rem] text-star-3'>
                                                        {`Rises ${formatTime(planet.rise)} · sets ${formatTime(planet.set)}`}
                                                    </span>
                                                    <span className='block text-[0.7rem] text-star-3'>
                                                        {`Highest in the ${directionWords(planet.bestAz)}, ${Math.round(planet.bestAlt)}°`}
                                                    </span>
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                {hiddenPlanets.length > 0 && (
                                    <p className='mt-3 text-sm text-star-3'>
                                        {`Not up in darkness tonight: ${hiddenPlanets.map(planet => planet.name).join(', ')}.`}
                                    </p>
                                )}
                            </div>

                            <Row label='Best viewing window' onClick={() => setDetail({ kind: 'window' })}>
                                <span className='block font-heading text-3xl font-light text-star'>
                                    {viewingWindow ? `${formatTime(viewingWindow.start)} to ${formatTime(viewingWindow.end)}` : night ? 'No clear dark window' : '—'}
                                </span>
                                <span className='data-mono mt-1 block text-star-3'>
                                    {viewingWindow ? `${formatDuration((viewingWindow.end.getTime() - viewingWindow.start.getTime()) / MINUTE)} of the darkest, Moon-free sky${outlook?.available ? ', allowing for cloud' : ''}` : ''}
                                </span>
                            </Row>

                            <Row label='Viewing quality' onClick={() => setDetail({ kind: 'conditions' })}>
                                <span className='grid items-center gap-6 sm:grid-cols-[minmax(0,13rem)_1fr]'>
                                    <ViewingGauge score={score} verdict={outlookState === 'loading' ? 'Reading forecast' : verdictFor(score)} />
                                    <span className='space-y-1.5'>
                                        <span className='data-mono block text-star-2'>
                                            {outlook?.available ? `Cloud ${outlook.cloud}% · ${outlook.summary}` : outlookState === 'loading' ? 'Fetching the forecast' : outlook?.reason === 'beyond' ? 'Forecast opens about nine days ahead' : outlook?.reason === 'past' ? 'No cloud forecast for a past night' : 'Forecast unavailable right now'}
                                        </span>
                                        {outlook?.available && (
                                            <>
                                                <span className='data-mono block text-star-3'>
                                                    {`Seeing ${outlook.seeing}/5 · steadiness of the air`}
                                                </span>
                                                <span className='data-mono block text-star-3'>
                                                    {`Transparency ${outlook.transparency}/5 · clarity`}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                </span>
                            </Row>
                        </div>

                        {variant === 'home' && (
                            <FadeLink href='/sky-tonight' className='group mt-8 inline-flex items-center gap-3 text-star'>
                                <span className='h-px w-8 bg-brass transition-all duration-500 group-hover:w-12' />
                                <span className='label-mono'>
                                    Full sky guide and hourly forecast
                                </span>
                            </FadeLink>
                        )}
                    </div>
                </div>
            </div>

            <Sheet
                open={detail !== null}
                onClose={() => setDetail(null)}
                eyebrow={nightKey ? formatKey(nightKey, 'long') : undefined}
                title={sheetTitle}
            >
                {detail?.kind === 'planet' && selectedPlanet && (
                    <PlanetDetail planet={selectedPlanet} body={selectedBody} time={time} />
                )}

                {detail?.kind === 'moon' && moon && night && (
                    <div>
                        <div className='mx-auto w-48'>
                            <MoonSphere phaseAngle={moon.phaseAngle} waxing={moon.waxing} />
                        </div>
                        <p className='mt-6 text-star-2'>
                            {moon.fraction > 0.85
                                ? 'A bright Moon washes out faint galaxies and nebulae, but it is the best time to study the Moon itself along the edge of its shadow.'
                                : moon.fraction < 0.2
                                    ? 'A thin or absent Moon: the darkest skies of the month, best for the Milky Way and faint deep-sky objects.'
                                    : 'Look along the terminator, the line between day and night on the Moon, where low sunlight throws long shadows from crater walls.'}
                        </p>
                        <dl className='mt-6'>
                            <DataPair term='Phase' value={moon.name} />
                            <DataPair term='Illuminated' value={`${Math.round(moon.fraction * 100)}%`} />
                            <DataPair term='Age' value={`${moon.age.toFixed(1)} days`} />
                            <DataPair term='Moonrise' value={formatTime(night.events.moonrise)} />
                            <DataPair term='Moonset' value={formatTime(night.events.moonset)} />
                            <DataPair term='Distance' value={`${Math.round(moon.distanceKm).toLocaleString('en-GB')} km`} />
                            <DataPair term='Next full Moon' value={moon.nextFull ? formatKey(dateKey(moon.nextFull)) : '—'} />
                            <DataPair term='Next new Moon' value={moon.nextNew ? formatKey(dateKey(moon.nextNew)) : '—'} />
                        </dl>
                    </div>
                )}

                {detail?.kind === 'sun' && night && (
                    <SunDetail events={night.events} />
                )}

                {detail?.kind === 'window' && night && (
                    <div>
                        <p className='font-heading text-4xl font-light text-star'>
                            {viewingWindow ? `${formatTime(viewingWindow.start)} to ${formatTime(viewingWindow.end)}` : 'No clear window'}
                        </p>
                        <p className='mt-5 text-star-2'>
                            We look for the longest stretch of the night when the Sun is well below the horizon, the Moon is down or thin, and the forecast cloud is lowest. That is when faint things show best.
                        </p>
                        <dl className='mt-6'>
                            <DataPair term='Full darkness' value={`${formatTime(night.events.darkStart)} to ${formatTime(night.events.darkEnd)}`} />
                            <DataPair term='Moon' value={`${formatTime(night.events.moonrise)} rise · ${formatTime(night.events.moonset)} set`} />
                            <DataPair term='Moonlight' value={moon ? `${Math.round(moon.fraction * 100)}% lit` : '—'} />
                            <DataPair term='Cloud in the window' value={outlook?.available ? `${outlook.cloud}% average` : 'No forecast'} />
                        </dl>
                        <FadeLink href='/telescope-nights' className='label-mono mt-8 inline-flex h-11 items-center rounded-full bg-brass px-6 text-ink'>
                            Join a telescope night
                        </FadeLink>
                    </div>
                )}

                {detail?.kind === 'conditions' && (
                    <ConditionsDetail outlook={outlook} state={outlookState} score={score} />
                )}
            </Sheet>
        </section>
    )
}

const PlanetDetail = ({ planet, body, time }: { planet: PlanetNight, body: { alt: number, az: number, distanceAU: number, diameter: number, magnitude: number, phaseFraction: number } | null, time: Date | null }): ReactNode => {
    const lightMinutes = body ? (body.distanceAU * AU_KM) / 17987547.48 : null

    return (
        <div>
            <p className='text-star-2'>
                {planet.visibleFrom
                    ? `${planet.name} is up in darkness from ${formatTime(planet.visibleFrom)} to ${formatTime(planet.visibleTo)}, highest in the ${directionWords(planet.bestAz)} at ${Math.round(planet.bestAlt)}° above the horizon. Look for a ${planet.look}.`
                    : `${planet.name} does not climb clear of the horizon in darkness tonight.`}
            </p>
            <dl className='mt-6'>
                <DataPair term='In the constellation' value={planet.constellation} />
                <DataPair term='Brightness' value={`Magnitude ${planet.magnitude.toFixed(1)}`} />
                {body && time && (
                    <DataPair term={`At ${formatTime(time)}`} value={body.alt > 0 ? `${Math.round(body.alt)}° up, ${directionWords(body.az)}` : 'Below the horizon'} />
                )}
                <DataPair term='Rises' value={formatTime(planet.rise)} />
                <DataPair term='Sets' value={formatTime(planet.set)} />
                {body && (
                    <>
                        <DataPair term='Distance from Earth' value={`${body.distanceAU.toFixed(2)} AU`} />
                        <DataPair term='Light travel time' value={lightMinutes ? `${lightMinutes < 90 ? `${lightMinutes.toFixed(1)} minutes` : `${(lightMinutes / 60).toFixed(1)} hours`}` : '—'} />
                        <DataPair term='Apparent size' value={`${body.diameter.toFixed(1)} arcseconds`} />
                        <DataPair term='Disc lit' value={`${Math.round(body.phaseFraction * 100)}%`} />
                    </>
                )}
            </dl>
            <FadeLink href='/solar-system' className='label-mono mt-8 inline-flex items-center gap-3 text-star'>
                <span className='h-px w-8 bg-brass' />
                See it in the orrery
            </FadeLink>
        </div>
    )
}

const SunDetail = ({ events }: { events: NightEvents }): ReactNode => {
    const darkMinutes = events.darkness === 'astronomical' ? (events.darkEnd.getTime() - events.darkStart.getTime()) / MINUTE : 0

    return (
        <div>
            <p className='text-star-2'>
                After sunset the sky darkens in three steps. Civil twilight ends when the Sun is 6° below the horizon, nautical at 12°, and astronomical at 18°, when the sky is as dark as it will get.
            </p>
            <dl className='mt-6'>
                <DataPair term='Sunset' value={formatTime(events.sunset)} />
                <DataPair term='Civil dusk' value={formatTime(events.civilDusk)} />
                <DataPair term='Nautical dusk' value={formatTime(events.nauticalDusk)} />
                <DataPair term='Astronomical dusk' value={formatTime(events.astroDusk)} />
                <DataPair term='Astronomical dawn' value={formatTime(events.astroDawn)} />
                <DataPair term='Nautical dawn' value={formatTime(events.nauticalDawn)} />
                <DataPair term='Civil dawn' value={formatTime(events.civilDawn)} />
                <DataPair term='Sunrise' value={formatTime(events.sunrise)} />
                <DataPair term='Full darkness' value={darkMinutes > 0 ? formatDuration(darkMinutes) : 'None tonight'} />
            </dl>
        </div>
    )
}

const ConditionsDetail = ({ outlook, state, score }: { outlook: Outlook | null, state: string, score: number | null }): ReactNode => {
    if (state === 'loading') {
        return (
            <p className='label-mono slow-pulse text-star-3'>
                Fetching the forecast
            </p>
        )
    }

    if (!outlook?.available) {
        return (
            <div>
                <p className='text-star-2'>
                    {outlook?.reason === 'beyond'
                        ? 'Cloud forecasts for this night are not published yet. They open about nine days ahead; until then the best viewing window allows only for darkness and moonlight.'
                        : outlook?.reason === 'past'
                            ? 'This night has already passed, so there is no cloud forecast for it. Darkness, moonlight and the planets are still calculated for the date.'
                            : 'We could not reach the weather service just now. Darkness and moonlight are still calculated; try again in a few minutes for cloud.'}
                </p>
            </div>
        )
    }

    return (
        <div>
            <div className='mx-auto max-w-[16rem]'>
                <ViewingGauge score={score} verdict={verdictFor(score)} />
            </div>
            <p className='mt-6 text-star-2'>
                Cloud matters most. Seeing is how steady the air is, which sets how sharp planets look; transparency is how clear it is, which decides how faint you can see. Both are estimated from the forecast wind and humidity.
            </p>
            <div className='mt-6 overflow-x-auto'>
                <table className='w-full min-w-[26rem] text-left'>
                    <thead>
                        <tr className='label-mono text-[0.62rem] text-star-3'>
                            <th scope='col' className='py-2 font-normal'>
                                Time
                            </th>
                            <th scope='col' className='py-2 font-normal'>
                                Cloud
                            </th>
                            <th scope='col' className='py-2 font-normal'>
                                Seeing
                            </th>
                            <th scope='col' className='py-2 font-normal'>
                                Clarity
                            </th>
                            <th scope='col' className='py-2 font-normal'>
                                Temp
                            </th>
                            <th scope='col' className='py-2 font-normal'>
                                Wind
                            </th>
                        </tr>
                    </thead>
                    <tbody className='data-mono text-star-2'>
                        {outlook.hours.map(hour => (
                            <tr key={hour.time} className='border-t border-hair'>
                                <td className='py-2 text-star'>
                                    {formatTime(new Date(hour.time))}
                                </td>
                                <td className='py-2'>
                                    <span className='inline-flex items-center gap-2'>
                                        <span className='inline-block h-1 w-10 bg-ink-4'>
                                            <span className='block h-1 bg-star-3' style={{ width: `${hour.cloud}%` }} />
                                        </span>
                                        {`${hour.cloud}%`}
                                    </span>
                                </td>
                                <td className='py-2'>
                                    {`${hour.seeing}/5`}
                                </td>
                                <td className='py-2'>
                                    {`${hour.transparency}/5`}
                                </td>
                                <td className='py-2'>
                                    {`${Math.round(hour.temperature)}°C`}
                                </td>
                                <td className='py-2'>
                                    {`${Math.round(hour.wind * 2.237)} mph`}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className='mt-6 text-xs text-star-3'>
                Forecast data from MET Norway, refreshed every half hour.
            </p>
        </div>
    )
}
